import type { Json } from "./json";

// ── View modes ──────────────────────────────────────────────────────────────
export const VIEW_MODES = ["list", "kanban", "calendar", "form"] as const;
export type ViewMode = (typeof VIEW_MODES)[number];

export const MULTI_RECORD_VIEW_MODES = ["list", "kanban", "calendar"] as const;
export type MultiRecordViewMode = (typeof MULTI_RECORD_VIEW_MODES)[number];

export function isViewMode(value: string): value is ViewMode {
    return (VIEW_MODES as readonly string[]).includes(value);
}

export function isMultiRecordViewMode(value: string): value is MultiRecordViewMode {
    return (MULTI_RECORD_VIEW_MODES as readonly string[]).includes(value);
}

// ── Records & queries ────────────────────────────────────────────────────────
export type RecordUuid = string;

export interface RecordData {
    id: RecordUuid;
    [field: string]: Json | undefined;
}

// Search domain — backend triple form: [field, operator, value], joined by "&" | "|" | "!".
export type DomainLeaf = [string, string, Json];
export type DomainOperator = "&" | "|" | "!";
export type DomainTerm = DomainLeaf | DomainOperator;
export type Domain = DomainTerm[];

export interface ListQuery {
    limit?: number;
    offset?: number;
    order?: string;
    search?: string;
    domain?: Domain;
    groupby?: string[];
}

// One server-computed group (presentation.action_records grouped path).
// Every possible value of the grouping axis is enumerated — enum options and
// comodel records included even when empty — with the TRUE per-group count
// and up to one page of records.
export interface ServerRecordGroup {
    value: Json;
    /** Server-resolved label (enum option label / comodel display name). */
    label: string | null;
    count: number;
    records: RecordData[];
}

export interface ListResult {
    records: RecordData[];
    total?: number;
    /** True when the response is the grouped shape (groups[] carries the data). */
    grouped?: boolean;
    groups?: ServerRecordGroup[];
}

// ── Workspace bootstrap ──────────────────────────────────────────────────────
export interface AppEntry {
    id: string;
    key: string;
    name: string;
    icon?: string;
    category?: string;
    sequence?: number;
    color?: number;
}

export interface MenuEntry {
    id: string;
    name: string;
    icon?: string;
    sequence?: number;
    actionKey?: string;
    action?: ActionDef;
    children?: MenuEntry[];
}

export interface ActionDef {
    key: string;
    name: string;
    modelKey?: string;
    defaultView?: ViewMode;
    availableViews?: ViewMode[];
    clientComponent?: string | null;
    // Action's domain template (the string from ir.action.domain). May contain
    // `active_id` placeholders that the list-drill renderer substitutes at
    // render-time from the closest upstream RecordFrame. Empty / "[]" when
    // the action is unfiltered.
    domain?: string;
    /** How the action opens. `current` routes through the workspace as a
        full-page view (URL changes). `dialog` mounts the action inside a
        Radix Dialog overlay without navigation. `inline` is reserved for
        future host-embedded views. */
    target?: "current" | "dialog" | "inline";
}

export interface BootstrapPayload {
    apps: AppEntry[];
    menus: Record<string, MenuEntry[]>;
    pendingApprovalCount: number;
    featureFlags: Record<string, boolean>;
}

// ── Identity & organizations ─────────────────────────────────────────────────
export interface OrganizationRef {
    id: string;
    code: string;
    name: string;
    country?: string;
    countryCode?: string;
    timezone?: string;
    imageAvatar?: string | null;
    parentId?: string | null;
}

export interface UserIdentity {
    userId: string;
    name: string;
    email?: string;
    timezone?: string;
    languageId?: string | null;
    imageAvatar?: string | null;
    allowedOrganizations: OrganizationRef[];
    activeOrganizationId: string;
    /** RBAC role codes the user carries (e.g. ["sales_rep", "ir.role.admin"]).
        Populated from the backend principal's `role_codes` slot. Empty
        list when no roles assigned. Used by:
        - View predicates: `<field visible-when="roles('sales_manager')"/>`
        - Client-action permission gating: `descriptor.requires: ["…"]`
        - Frontend permission checks via `hasRoles(session, codes)`. */
    roles: string[];
}

// ── Localization (res.language) ──────────────────────────────────────────────
export type TextDirection = "ltr" | "rtl";

export interface LanguageRef {
    id: string;
    code: string;
    name?: string;
    nativeName?: string;
    direction: TextDirection;
}

// ── View metadata (server-described views) ───────────────────────────────────
export interface RenderPlan {
    viewType: string;
    modelKey: string;
    /** XML view id (backend render plan `view_id`). */
    viewId?: string;
    /** Primary `ir.application.view` row uuid (backend `view_parent_id`) — the
        parent_id a confirm card authors an extension against; null for
        file-only views. foundation.customization Phase 4C. */
    viewParentId?: string | null;
    [key: string]: Json | undefined;
}

// ── Action load (the /api/web/{slug}/load payload, normalized) ────────────────
export interface FieldDef {
    name: string;
    type: string;
    label?: string;
    required?: boolean;
    readonly?: boolean;
    selection?: [string, string][];
    // Relational target model key for `reference` / `many2many` / `one2many`
    // fields — drives RelationListView's child-action lookup. Backend ships
    // it via `FieldSpec.to_ui_dict()`.
    targetModel?: string;
    // foundation.workflow Phase 2 marker — when true, the field is
    // governed by the workflow engine. The save path strips it from the
    // dirty-filtered payload (the ORM write-guard would reject a direct
    // update) and required-validation skips it (filled by backend default
    // at create-time; transitions run via `env.workflow.transition()`).
    workflow?: boolean;
    // Static default value for the field, surfaced from the kernel field spec
    // (`to_ui_dict`). Present only for scalar fields with a non-null default.
    // Used to pre-fill the draft when a `/new` form opens — the same value the
    // backend create path writes, so form state matches stored state.
    defaults?: Json;
    // foundation.customization Phase 4 — synthesized custom-property field.
    // When `isProperty` is set, the field's value lives at `record[bag][key]`
    // (a JSONB column) rather than at a top-level column. `name` is "<bag>:<key>".
    isProperty?: boolean;
    bag?: string;
    propertyKey?: string;
    help?: string;
}

export interface ListColumn {
    name: string;
    label: string;
    widget?: string;
    optional?: string;
    invisible: boolean;
    // XML `display_format="…"` — re-renders m2o/x2m chip labels from the
    // chip's `data` payload (e.g. "{name}" drops a "[CODE] " prefix).
    displayFormat?: string | null;
    // Widget options collected by the DSL parser from `option-*` attributes
    // (e.g. `option-avatar-field="image_avatar"` → options.avatar_field).
    // Keys are snake_case; values are the raw attribute strings.
    options?: Record<string, string>;
}

// Row action buttons declared via <button> directly under <ListView> (or nested
// inside <group>). Each renders as an icon-or-label button in the trailing
// actions column of every row, dispatching `command` against the row's record
// when clicked. Mirrors the FormView button grammar but in row-scope.
// `commandType` selects which engine the central dispatcher routes through.
// Default (omitted) = run as a regular model command via the action service.
// "workflow" = treat `command` as a workflow transition code, dispatch via
// the workflow service. "approval" = dispatch via the approval service.
// Add new engine keys here as engines come online.
// `command-type` on a DSL `<button>` — selects which CommandEngine handles
// the dispatch in `core/command/engines.ts`.
//   - "workflow"  → workflowEngine routes through `/api/workflow/transition`
//   - "approval"  → approvalEngine consumes the case-decision verb
//   - "report"    → reportEngine routes through the report runner
//   - "action"    → actionEngine resolves commandName as an ir.action path
//                    slug; reads action.target → OPEN_ACTION_DIALOG or
//                    OPEN_ACTION (foundation.presentation Enh 08)
// When unset / unrecognised, the default engine handles via
// `/api/web/<actionSlug>/<recordRef>/<commandName>`.
export type ButtonCommandType = "workflow" | "approval" | "report" | "action";

export interface ListRowButton {
    name: string;
    label?: string | null;
    icon?: string | null;
    command?: string | null;
    commandType?: ButtonCommandType | null;
    special?: string | null;
    // DSL `context="…"` — extra JSON payload threaded into the RUN_BUTTON
    // command's `context` slot. The string is parsed (JSON or Python-literal
    // tolerant) at dispatch time; falls back to null on parse failure.
    context?: string | null;
    // Predicate expression evaluated per row; when true the button is hidden
    // for that record. Empty / null = always visible.
    invisible?: string | null;
    confirm?: string | null;
}

export interface ListRenderPlan {
    viewType: "list";
    modelKey: string;
    viewId?: string;
    viewParentId?: string | null;
    orderBy?: string;
    // DSL's `default_group_by` attribute (comma-separated → ordered field list).
    // Applied once when the action loads if the user hasn't overridden it.
    defaultGroupBy?: string[];
    columns: ListColumn[];
    rowButtons: ListRowButton[];
    // DSL `editable="top"` / `editable="bottom"` — when set, the list is
    // inline-editable: double-click a cell to enter edit mode. The value
    // also drives the "+New record" affordance row placement (Phase 4.3
    // add-line work). Empty string = read-only list (default).
    editable?: "top" | "bottom" | "";
}

export interface SearchFilter {
    key: string;
    label: string;
    domain: string; // JSON-encoded domain leaves
    // Optional ad-hoc metadata: when a filter is created from a free-text
    // search (typing in the input + picking a search field), the field's
    // human label and the typed text are preserved so the chip can render as
    // "FieldName: keywords" with the field part muted. Absent on DSL-defined
    // or saved-as-favourite filters.
    fieldLabel?: string;
    text?: string;
}

// A predefined group-by preset from the search view DSL.
// `fields` is the ordered list of axes the preset applies (single-axis presets
// are just a one-element array). Clicking the preset replaces the active
// group-by stack with these fields.
export interface GroupByOption {
    fields: string[];
    label: string;
}

// A typeahead-scoped search field: typing in the search box offers one
// suggestion per searchfield ("Name: foo", "Code: foo"), and selecting a
// suggestion produces a pill chip with the substituted domain.
export interface SearchField {
    name: string;
    label: string;
    filterDomain: string; // JSON template, "{self}" → the typed text
}

export interface SearchPlan {
    searchfields: SearchField[];
    filters: SearchFilter[];
    groupbys: GroupByOption[];
    hasActive: boolean;
}

// Kanban card template is a recursive node tree (KanbanCard › Title/Subtitle/
// Row/Footer › field|button). Fields are leaves carrying name + widget.
export interface KanbanNode {
    type: string;
    name?: string;
    widget?: string;
    invisible?: boolean;
    attrs?: Record<string, Json>;
    children?: KanbanNode[];
}

export type KanbanDragMode = "field" | "workflow" | "auto";

export interface KanbanPlan {
    modelKey: string;
    viewId?: string;
    viewParentId?: string | null;
    defaultGroupBy: string;
    orderBy?: string;
    onDrag: KanbanDragMode;
    quickCreate: boolean;
    allowReorder: boolean;
    cardTemplate: KanbanNode | null;
    fieldNames: string[];
}

// ── Form RenderPlan ──────────────────────────────────────────────────────────
// Parsed shape of a <FormView> DSL definition. Mirrors the backend output of
// `_parse_form_view` — `header[]`, `sheet[]`, `footer[]`, `activity[]` are each
// ordered element lists; elements are tagged unions so renderers can branch by
// `type`. This contract covers ONLY the surface needed for the form-header
// port (Phase 4 step 1) — sheet/section/notebook/property are still
// here for forward compat but their renderers land alongside the form body.

export type FormElement =
    | FormFieldElement
    | FormPropertyElement
    | FormButtonElement
    | FormSectionElement
    | FormNotebookElement
    | FormStatusbarElement
    | FormDecisionElement
    | FormRelatedElement
    | FormSeparatorElement;

// `<decision title="…"/>` — AI Insights deterministic decision cockpit card
// (foundation.ai_insights). Self-closing leaf in the sheet. The DecisionCard
// widget evaluates the current record via POST /api/insights/evaluate after
// load and renders score · severity · ranked factors · recommended actions.
export interface FormDecisionElement {
    type: "decision";
    title?: string | null;
    invisible?: string | null;
}

// `<separator string="…" id="…" invisible="…"/>` — standalone labeled
// divider between controls. With `string` it renders in the section-title
// style (uppercase accent label + hairline rule); without it, a plain
// full-width rule. `id` is xpath-targetable for cross-module <extend>.
export interface FormSeparatorElement {
    type: "separator";
    string?: string | null;
    id?: string | null;
    invisible?: string | null;
}

// `<related>` block (Enhancement 06) — renders as a `Related ▾` toolbar
// dropdown. Each chip points at a bespoke ir.action by its path slug; the
// action carries the domain template with `active_id` placeholders.
export interface FormRelatedChip {
    name: string;                     // relational field on the current model
    action?: string | null;           // optional. Mode B: bound to an ir.action by xml_id (path / domain / model_key flow through related_counts). Mode A: omit — frontend uses targetModelKey for an action-less drill (M2O: target record from parent's field value).
    targetModelKey?: string | null;   // auto-derived by load_action from the field-spec on the owning model. Drives Mode A drill URLs (M2O → `/<targetModelKey>/<id>/form`).
    fieldLabel?: string | null;       // auto-derived by load_action from the field-spec's `label=` (e.g. "Tags"). Used as the chip's display label when no `string=` was set in the DSL — keeps the dropdown from reading "tag_ids".
    icon?: string | null;             // Lucide icon name; falls back to menu-entry icon → "link-2"
    string?: string | null;           // explicit DSL override of the chip label; wins over fieldLabel
    hideEmpty: boolean;               // default true; chip hidden when count===0
}

export interface FormRelatedElement {
    type: "related";
    items: FormRelatedChip[];
}

export interface FormFieldElement {
    type: "field";
    name: string;
    string?: string | null;
    widget?: string | null;
    invisible?: string | null;
    readonly?: boolean;
    required?: boolean;
    colspan?: number;
    nolabel?: boolean;
    /** Developer-supplied CSS class(es) from `<field class="…"/>` — appended to
        the field cell so authors can opt a value into emphasis (e.g. bold). */
    cssClass?: string | null;
    placeholder?: string | null;
    displayFormat?: string | null;
    // JSON-encoded relational filter template (e.g.
    //   `[['member_type','=','individual'],['parent_partner_id','=',partner_id]]`).
    // M2O / M2M pickers substitute parent-record field values into bare
    // identifiers + `$active_record.<field>` tokens at search time, then
    // pass the result to `/api/web/<slug>/related/<field>?domain=…`.
    domain?: string | null;
    // Inline list-view declaration nested under `<field><ListView>…</ListView></field>`.
    // When present, O2M / M2M fields render as an embedded table using these
    // columns instead of the child model's default list view. The backend
    // parser ships this as a fully-parsed `ListRenderPlan`-shaped dict.
    inlineListView?: ListRenderPlan | null;
    options?: Record<string, string>;
}

export interface FormButtonElement {
    type: "button";
    name: string;
    command?: string | null;
    commandType?: ButtonCommandType | null;
    label?: string | null;
    labelField?: string | null;
    icon?: string | null;
    invisible?: string | null;
    context?: string | null;
    confirm?: string | null;
    special?: string | null;
    specialOptions?: Record<string, string>;
}

export interface FormSectionElement {
    type: "section";
    cols?: number | null;
    string?: string | null;
    invisible?: string | null;
    elements: FormElement[];
}

export interface FormNotebookElement {
    type: "notebook";
    pages: FormNotebookPage[];
}

export interface FormNotebookPage {
    type: "page";
    label: string;
    icon?: string | null;
    invisible?: string | null;
    elements: FormElement[];
}

// `<statusbar field="..."/>` — workflow-driven stage pipeline. `workflowCode`
// and `allStages` are inlined by the backend action loader so the frontend
// can render the pill row from the plan alone (no extra fetch needed). The
// per-record CURRENT stage comes from `record[field]`.
export interface FormStatusbarElement {
    type: "statusbar";
    field: string;
    workflowCode?: string | null;
    allStages?: FormStatusbarStage[];
}

export interface FormStatusbarStage {
    code: string;
    label: string;
    isInitial?: boolean;
    isFinal?: boolean;
}

// `<property name="<bag>:<key>"/>` (foundation.customization Phase 4) — an
// anchored custom-property field placeable anywhere a `<field>` can go. The
// backend synthesizes a FieldDef named `"<bag>:<key>"` (carrying `isProperty`,
// `bag`, `propertyKey`) so the widget registry renders it like any field; the
// renderer reads/writes `record[<bag>][<key>]`.
export interface FormPropertyElement {
    type: "property";
    name: string;                     // "<bag>:<key>"
    bag: string;                      // JSONB column on the host model
    key: string;                      // property slug within the bag
    string?: string | null;
    widget?: string | null;
    invisible?: string | null;
    readonly?: boolean;
    required?: boolean | null;
    nolabel?: boolean;
    placeholder?: string | null;
    options?: Record<string, string>;
}

// `<summary>` (form-view revamp Enh 14) — scrolling header card above the sheet:
// title + subtitle (mixed literal text + <field> value tokens) + a KPI stat
// strip. Each KPI resolves via exactly one of field= (record value) / formula=
// (formula engine) / metric= (@api.metric scalar); `emphasis` drives the value
// colour (accent · good · warn · warn-below:N · warn-above:N).
export type FormSummaryToken =
    | { type: "text"; value: string }
    // `displayFormat` — per-token XML `display_format="…"` override for m2o/x2m
    // values (re-rendered from the chip's `data` payload, e.g. "{name}").
    // `icon` — lucide glyph name rendered before the value (subtitle only).
    // `chip` — tone pill wrapper: info | good | warn | bad | neutral.
    | { type: "field"; name: string; displayFormat?: string | null; icon?: string | null; chip?: string | null };

export interface FormKpiElement {
    field?: string | null;
    metric?: string | null;
    formula?: string | null;
    label?: string | null;
    emphasis?: string | null;
}

export interface FormSummaryElement {
    eyebrow: FormSummaryToken[];
    title: FormSummaryToken[];
    subtitle: FormSummaryToken[];
    kpis: FormKpiElement[];
}

// `<cockpit>` (form-view revamp Enh 14) — the record side pane. Each card is a
// capability gated by its module + model opt-in; an absent capability's card
// silently no-renders. The frontend dispatches each component through the
// cockpit-component registry; built-ins today are `status` + `quick-actions`,
// others register as their engines land (Stage 3+).
export interface CockpitButton {
    command?: string | null;
    clientAction?: string | null;
    label?: string | null;
    icon?: string | null;
    invisible?: string | null;
    confirm?: string | null;
}

export interface CockpitStatusCard {
    type: "status";
    field: string | null;
    showAge: boolean;
    buttons: CockpitButton[];
    /** Auto-derive only (frontend): ordered/visible enum keys mirrored from a
        `<field widget="statusbar" statusbar_visible="a,b,c">` header, so the
        cockpit's enum rail shows the same subset + order the header bar did.
        null/absent → the rail shows every selection option. */
    statusbarVisible?: string[] | null;
}

export interface CockpitQuickActionsCard {
    type: "quick-actions";
    actions: CockpitButton[];
}

export interface CockpitChecklistCard {
    type: "checklist";
    stageGate: boolean;
    ref: string | null;
}

export interface CockpitGenericCard {
    type: "card";
    key: string | null;
    attrs: Record<string, string>;
}

export interface CockpitSimpleCard {
    type: "decision" | "assistant" | "suggestions" | "tasks";
}

export type FormCockpitComponent =
    | CockpitStatusCard
    | CockpitQuickActionsCard
    | CockpitChecklistCard
    | CockpitGenericCard
    | CockpitSimpleCard;

export interface FormCockpitElement {
    auto: boolean;
    width: string | null;
    components: FormCockpitComponent[];
}

export interface FormRenderPlan {
    viewType: "form";
    modelKey: string;
    viewId?: string;
    viewParentId?: string | null;
    create: boolean;
    edit: boolean;
    delete: boolean;
    header: FormElement[];
    summary?: FormSummaryElement | null;
    sheet: FormElement[];
    cockpit?: FormCockpitElement | null;
    footer: FormElement[];
    activity: string[];
}

// ── ClientAction layout (Enhancement 08 — layout-XML slice) ─────────────────
// `<ClientActionView>` DSL parses to this shape. Three structural slots
// (header / body / footer) host typed items the LayoutRenderer walks.
// Buttons reuse FormButtonElement verbatim — view-agnostic chrome.

export interface ClientActionContainerElement {
    type: "container";
    /** Imperative DOM slot — the registered client component grabs the
        rendered element via `refs[ref]` in onMount and mounts external
        libraries into it (three.js scene, Leaflet map, AG-Grid, …). */
    ref: string;
    class?: string;
}

export interface ClientActionComponentElement {
    type: "component";
    /** Placeholder slot — the registered React component mounts here
        when the layout has a `<component/>` element. Pure-React
        client actions (no imperative refs needed) use this slot. */
}

export interface ClientActionHintElement {
    type: "hint";
    text: string;
}

export interface ClientActionRibbonElement {
    type: "ribbon";
    label: string;
    tone?: "success" | "info" | "warning" | "danger" | "neutral";
    visibleWhen?: string;
    hiddenWhen?: string;
}

export type ClientActionElement =
    | FormButtonElement
    | ClientActionContainerElement
    | ClientActionComponentElement
    | ClientActionHintElement
    | ClientActionRibbonElement;

export interface ClientActionRenderPlan {
    viewType: "clientaction";
    modelKey: string;
    title?: string;
    size?: "sm" | "md" | "lg" | "xl" | "full";
    header: ClientActionElement[];
    body: ClientActionElement[];
    footer: ClientActionElement[];
}

// A saved/predefined search filter (ir.data.filter): predefined "filter" rows
// come from the view DSL; "favorite" rows are user-created.
export interface SavedFilter {
    id: string;
    name: string;
    filterType: "filter" | "groupby" | "favorite";
    domain: string;
    groupby?: string;
    isShared?: boolean;
}

export interface BoundAction {
    /** URL path slug used to load + open the action. */
    slug: string;
    name: string;
    /** "dialog" | "current" — Enh 10 record-bound actions open as dialogs. */
    target: string;
    /** JSON context template; `active_id` is substituted with the open
        record's uuid client-side (e.g. {"default_user_id":"active_id"}). */
    context: string;
}

export interface ActionLoad {
    action: ActionDef;
    fieldDefs: FieldDef[];
    listPlan: ListRenderPlan | null;
    searchPlan: SearchPlan;
    kanbanPlan: KanbanPlan | null;
    formPlan: FormRenderPlan | null;
    // Field names declared in each view's DSL — backend computes this in
    // load_action via `_extract_field_names_from_render_plan`. The save
    // path (WS 4.1) reads `viewFieldNames.form` so list-view-only columns
    // don't get included in the form-save payload.
    viewFieldNames: { list?: string[]; form?: string[]; kanban?: string[] };
    /** Enh 10 — record-bound server actions for this action's model, surfaced
        in the form's Actions ▾ menu. Empty unless actions declare
        bind_model_action=True + bind_model_key=<this model>. */
    boundActions: BoundAction[];
}
